/**
 * Onboarding spec — the production source of truth for subsection
 * copy, slot structure, treatments, and step dependencies.
 *
 * Lifted out of `mockScenarios.ts` so the live renderer no longer
 * depends on a "mock" file. The mock scenarios consume this baseline
 * + overlay scenario-specific state on top; the live path
 * (`scenarioFromServer`) consumes the same baseline + overlays the
 * BE step statuses.
 *
 * **Dependency rules**: the agreement subsections' `preconditionIds`
 * MUST stay in sync with yaas-api's `AGREEMENT_PREREQUISITE_STEPS`
 * (see `stepPayloadSchemas/index.ts`), which is what `getStepRequires`
 * returns for both `msa_signing` and `minting_agreement_signing`. When
 * the BE list changes, update this file. A mismatch surfaces as the BE
 * rejecting a send with "… has unmet step dependencies" while the FE
 * shows the card as ready — drift is loud.
 */

import type { OnboardingSubsection } from "../types";

// Compact spec — only fields that differ from zero / undefined.
type SubsectionSeed = Omit<
    OnboardingSubsection,
    "satisfiedProofPoints" | "outstandingProofPoints" | "pendingReviewProofPoints"
> & {
    satisfiedProofPoints?: number;
    outstandingProofPoints?: number;
    pendingReviewProofPoints?: number;
};

function s(seed: SubsectionSeed): OnboardingSubsection {
    return {
        satisfiedProofPoints: 0,
        outstandingProofPoints: 0,
        pendingReviewProofPoints: 0,
        ...seed
    };
}

/**
 * Mirrors yaas-api `AGREEMENT_PREREQUISITE_STEPS`. Kept here as a typed
 * list (not a string array) so a typo on a subsection id fails to
 * compile. Update both when the BE dependency graph changes.
 *
 * Both §D.1 and §D.2 gate on this one list, because the BE gates both on
 * it: the Minting Master Agreement is a separate contract with a separate
 * counterparty (the Mint/Redeem Operator), not a document the MSA has to
 * precede. Listing "msa" here would put the applicant back to waiting for
 * a counter-signature before they could send themselves the second
 * signing email; both envelopes are meant to be out at the same time.
 */
const AGREEMENT_PRECONDITION_IDS: OnboardingSubsection["preconditionIds"] = [
    "use-case",
    "legal-entity",
    "tax-identification",
    "signatory-authority",
    "beneficial-ownership",
    "source-of-funds",
    "wallet-primary"
];

/**
 * Canonical Path A baseline — the subsection list every applicant
 * starts from before scenario / live overlays apply.
 */
export function baselineSubsections(): OnboardingSubsection[] {
    return [
        // ─── §A — Use Case & Intent ──────────────────────────────────────
        s({
            id: "use-case",
            category: "use-case",
            number: "§A",
            title: "Use Case & Intent",
            description:
                "A few selections about your intended use, expected volume, and whether you need redemption as well as minting. Drives which downstream proofs apply.",
            // No file uploads — the section is a single 3-question form
            // rendered via a custom panel (UseCaseForm). Slots stay empty
            // so no sub-rows appear in the left list.
            slots: [],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~5 min",
            readinessHint:
                "A sense of the volume you'll mint, and whether redemption is in scope. Redemption is not granted by default — it has to be requested here and specified on the Minting Master Agreement.",
            totalProofPoints: 3,
            proofExamples: ["Use cases stated", "Volume range stated", "Redemption need stated"],
            isApplicable: true
        }),

        // ─── §B — Organization & Entity ──────────────────────────────────
        s({
            id: "legal-entity",
            category: "organization",
            number: "§B.1",
            title: "Legal Entity",
            description: "Gather details about your entity and its operations.",
            slots: [
                {
                    title: "Entity Details",
                    description:
                        "The entity's legal name, any trading name, where it was formed, and who we should contact about this onboarding.",
                    // Each sub-row saves as one patch, so its fields are
                    // present together or not at all.
                    fulfilment: {
                        fields: [
                            "legalName",
                            "entityType",
                            "registeredAddress",
                            "companyNumber",
                            "incorporationJurisdiction"
                        ]
                    }
                },
                {
                    title: "Entity Contacts",
                    description: "Who we speak to day to day, and where contractual notices are served.",
                    fulfilment: {
                        fields: ["primaryContactName", "primaryContactEmail", "legalContactName", "legalContactEmail"]
                    }
                },
                {
                    title: "Formation Document",
                    description:
                        "The official document issued by your jurisdiction's registry when the entity was created. It carries your legal name, formation date, and jurisdiction.",
                    acceptable: [
                        "Certificate of Incorporation (corporations)",
                        "Certificate of Formation / Articles of Organization (LLCs)",
                        "Memorandum & Articles of Association (Cayman / BVI / UK / similar)",
                        "Foundation Charter (foundations)",
                        "Trust Deed (trusts)",
                        "Equivalent constitutive document"
                    ],
                    examplesOnly: true,
                    fulfilment: { fields: ["formationDocumentUuid"] }
                },
                {
                    title: "Operating Document",
                    description:
                        "The internal rules of the entity, including who can act on its behalf and how contracts get executed. If your formation document is what created the entity, this is what says how it runs.",
                    acceptable: [
                        "Operating Agreement (LLC)",
                        "Bylaws (corporation)",
                        "Foundation Constitution (foundations)",
                        "Trust Deed (trusts)"
                    ],
                    examplesOnly: true,
                    fulfilment: { fields: ["operatingDocumentUuid"] }
                },
                {
                    title: "Certificate of Good Standing",
                    description:
                        "Confirms the entity is active today: filings are current, fees are paid, and there's no pending dissolution. Must be issued within the last 90 days. Your formation document proves the entity once existed; this one proves it still does.",
                    acceptable: ["Certificate of Good Standing", "Certificate of Existence", "Certificate of Status"],
                    examplesOnly: true,
                    fulfilment: { fields: ["certificateOfGoodStandingUuid"] }
                }
            ],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~10 min",
            readinessHint:
                "Formation document (Articles of Incorporation, Certificate of Formation, Memorandum & Articles, Foundation Charter, or equivalent), the entity's operating document (Operating Agreement, Bylaws, Foundation Constitution, or Trust Deed), and a current Certificate of Good Standing or equivalent (≤90 days).",
            totalProofPoints: 4,
            proofExamples: [
                "Entity details are on file",
                "Entity legally exists in its stated jurisdiction",
                "Internal rules of operation are on file",
                "Entity is currently in good standing"
            ],
            isApplicable: true
        }),
        s({
            id: "tax-identification",
            category: "organization",
            number: "§B.2",
            title: "Tax ID",
            description:
                "Confirms your entity has a registered tax identification number. Used for fee and yield reporting.",
            slots: [
                {
                    title: "Tax ID",
                    description:
                        "The official letter from your tax authority confirming the entity has been assigned a tax ID. One of the strongest cross-checks on the entity's existence and legal name.",
                    acceptable: ["IRS EIN assignment letter (US)", "Jurisdictional tax authority letter (non-US)"],
                    examplesOnly: true,
                    fulfilment: { fields: ["taxIdValue", "taxIdJurisdiction"] }
                }
            ],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~2 min",
            readinessHint: "EIN assignment letter (US) or jurisdictional tax authority letter (non-US).",
            totalProofPoints: 1,
            proofExamples: ["Entity has a valid tax identifier"],
            isApplicable: true
        }),
        s({
            id: "beneficial-ownership",
            category: "organization",
            number: "§B.5",
            title: "Ultimate Beneficiaries",
            slotsAsItems: true,
            description:
                "Add every individual with 25% or more ownership of your entity. Each submits a government photo ID and their ownership percentage.",
            slots: [],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~3 min per beneficiary",
            readinessHint:
                "Per beneficiary: full legal name, approximate ownership percentage, and a government photo ID (passport, national ID, or driver's license).",
            totalProofPoints: 0,
            proofExamples: ["Beneficiary is named and screened", "Ownership percentages disclosed"],
            isApplicable: true
        }),
        s({
            id: "sanctions-screening",
            category: "organization",
            number: "§B.6",
            title: "Sanctions, PEP & adverse-media screening",
            description:
                "Confirms your entity, every UBO, and the signatory clear OFAC, UN, EU, UK HMT, PEP, and adverse-media lists. Runs at submission and re-runs monthly. Nothing required on your side.",
            slots: [
                {
                    title: "Sanctions screening result",
                    description:
                        "Confirms the entity, every UBO, and the signatory don't appear on OFAC SDN, OFAC Consolidated, UN, EU, or UK HMT lists. Generated automatically on submission."
                },
                {
                    title: "PEP screening result",
                    description:
                        "Confirms no named individual is a politically-exposed person who would require enhanced due diligence. A hit doesn't disqualify automatically; it triggers a deeper review."
                },
                {
                    title: "Adverse-media screening result",
                    description:
                        "Confirms no entity or named individual surfaces in disqualifying public reporting (fraud, sanctions evasion, criminal allegations)."
                },
                {
                    title: "Re-screening log",
                    description:
                        "The ongoing record of monthly re-screens after activation. Sanctions and PEP lists update continuously; this log shows every check that's run over the lifetime of the relationship."
                }
            ],
            treatment: "solomon-run",
            status: "NotStarted",
            slaLabel: "Runs at submission · re-screened monthly",
            totalProofPoints: 6,
            proofExamples: [
                "Entity is not on any sanctions list",
                "Each individual is not on any sanctions list",
                "No PEP or adverse-media exposure"
            ],
            isApplicable: true
        }),
        s({
            id: "source-of-funds",
            category: "organization",
            number: "§B.7",
            title: "Source of Funds",
            description: "Captures where your funds come from and where you operate.",
            slots: [
                {
                    title: "Funds & Operations",
                    description:
                        "Two short narratives: where your treasury funds came from, and where you materially operate. A few sentences each is enough.",
                    fulfilment: { fields: ["sourceDescription"] }
                },
                {
                    title: "Supporting Documents (Optional)",
                    description: "Attach any supporting documents for your source of funds.",
                    fulfilment: { fields: ["supportingDocumentUuid"], optional: true }
                }
            ],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~5 min",
            readinessHint:
                "A short narrative on the origin of your treasury funds and where you primarily operate. Optionally, supporting documents (financial statements, bank statements, capital documentation).",
            totalProofPoints: 2,
            proofExamples: ["Source of treasury funds is disclosed", "Jurisdiction of primary operations is declared"],
            isApplicable: true
        }),

        // ─── §C — Addresses ─────────────────────────────────────────────
        s({
            id: "wallet-primary",
            category: "wallets",
            number: "§C.1",
            title: "Authorized Address Register",
            slotsAsItems: true,
            complianceQuestion:
                "Which exact addresses may mint or receive, and has this counterparty proved control of each one? This is where the entity-level checks bind to something enforceable — minting is restricted to verified addresses, so an unregistered address is simply rejected.",
            description: "Every address you will mint from or redeem to. Requires proving control over the address.",
            slots: [],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~5 min per address",
            readinessHint:
                "Per address: the address itself, and a way to prove control — either signing a message we issue, or sending a small transfer from it. A Squads multisig is proved by any member signer signing on the vault's behalf.",
            totalProofPoints: 0,
            proofExamples: ["Address declared and validated", "Control proved by signature or transfer"],
            isApplicable: true
        }),

        // ─── §S — Authorized Signatory ──────────────────────────────────
        // Sits between the addresses and the review because the signatory
        // is who executes what is about to be submitted, not entity
        // paperwork gathered alongside the formation documents.
        s({
            id: "signatory-authority",
            // Its own category so it lands here and nowhere else: the last
            // thing the applicant enters before reading the application
            // back. `number` stays §B.4 — that is the internal slug, not
            // the rendered position, which is computed from render order.
            category: "signatory",
            number: "§B.4",
            title: "Authorized Signatory",
            description:
                "Identifies the individual who will sign the agreement on the entity's behalf, and confirms they're formally authorized to do so.",
            slots: [
                {
                    title: "Signatory Details",
                    description: "Who will sign on the entity's behalf — their legal name, title, and email.",
                    fulfilment: {
                        fields: ["signatoryName", "signatoryEmail", "signatoryTitle"]
                    }
                },
                {
                    title: "Authorization Resolution",
                    description:
                        "Proves the entity has formally authorized this individual to sign on its behalf. A signature without backing authorization isn't binding on the entity.",
                    acceptable: [
                        "Board Resolution (corporations)",
                        "Manager Resolution (manager-managed LLC)",
                        "Member Resolution (member-managed LLC)",
                        "Foundation Council Resolution",
                        "Operating Agreement / Bylaws clause + Officer Certificate",
                        "Power of Attorney naming the signatory",
                        "Incumbency Certificate naming the signatory"
                    ],
                    // yaas-api completes `signatory_authority` on the
                    // details alone, so the resolution is an addition
                    // rather than a blocker.
                    fulfilment: { fields: ["authorizationDocumentUuid"], optional: true }
                }
            ],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~5 min",
            readinessHint:
                "A board / manager resolution naming the signatory. Their photo ID is already on file if they are also a beneficial owner.",
            totalProofPoints: 2,
            proofExamples: ["Signatory is identified", "Signatory is authorized to execute the MSA"],
            isApplicable: true
        }),

        // ─── §R — Review and submit ──────────────────────────────────────
        s({
            id: "review-submit",
            category: "review",
            number: "§R",
            title: "Review and submit",
            complianceQuestion:
                "Is this what the applicant meant to tell us? Everything above is entered section by section over days; this is the one place the whole submission is read as a single document before it is filed.",
            description:
                "Everything you have entered, in one place. Check it reads the way you intend, then submit. Solomon reviews the application and sends the agreements for signature once it is approved.",
            slots: [],
            treatment: "standard",
            status: "NotStarted",
            estimateLabel: "~5 min",
            readinessHint: "Every section above saved.",
            totalProofPoints: 0,
            isApplicable: true
        }),

        // ─── §D — Agreements ─────────────────────────────────────────────
        s({
            id: "msa",
            category: "agreements",
            number: "§D.1",
            title: "Master Services Agreement",
            description:
                "The agreement that governs your relationship with Solomon Labs. Solomon sends it for e-signature once your application is approved. By signing, you acknowledge the embedded disclosures (regulatory status, smart-contract risk, sanctions and AML, tax responsibility, reserve risk, termination rights, not-investment-advice), execute the compliance representations and warranties, and authorize the audit-trail entry. All bundled into the same signing event.",
            slots: [],
            verification: {
                provider: "an e-signature provider",
                ctaLabel: "Sign Agreement"
            },
            treatment: "standard",
            status: "NotStarted",
            // Mirrors yaas-api `getStepRequires("msa_signing")` — see the
            // AGREEMENT_PRECONDITION_IDS comment at the top of this file.
            preconditionIds: AGREEMENT_PRECONDITION_IDS,
            estimateLabel: "~10 min",
            readinessHint: "Your authorized signatory available to e-sign, once Solomon has approved the application.",
            totalProofPoints: 1,
            proofExamples: ["MSA executed by an authorized signatory"],
            isApplicable: true
        }),
        s({
            id: "order-form",
            category: "agreements",
            number: "§D.2",
            title: "Minting Master Agreement",
            description:
                "Sets out your specific minting access: the assets you may mint and redeem, your registered addresses, your limits, fees, and reporting. Signed with the Mint/Redeem Operator, so it goes out alongside the Master Services Agreement rather than after it. Neither waits on the other; both wait on your application being approved.",
            slots: [],
            verification: {
                provider: "an e-signature provider",
                ctaLabel: "Sign Agreement"
            },
            treatment: "standard",
            status: "NotStarted",
            preconditionIds: AGREEMENT_PRECONDITION_IDS,
            estimateLabel: "~15 min",
            readinessHint:
                "Your authorized signatory available to e-sign, and agreement on your limits, fees, and reporting cadence.",
            totalProofPoints: 1,
            proofExamples: ["Minting Master Agreement executed by both parties"],
            isApplicable: true
        })
    ];
}
